import { Injectable, Inject, EventEmitter } from '@angular/core';
import { Headers, Request, RequestMethod, BaseRequestOptions } from '@angular/http';
import { Observable, Observer, BehaviorSubject } from 'rxjs';
import { Store } from '@ngrx/store';
import { TeardownLogic } from 'rxjs/Subscription';

import { BrokerRequestHeader } from './broker.service';
import { BaseService } from './base.service';
import { MonitoredHttpService as Http, JWT_STATUS, CONNECTION_STATUS } from './monitored-http.service';

import { StorageService } from './storage.service';
import { UrlFactoryService } from './url-factory.service';
import { GUIDService } from './guid.service';

declare const window: any;

export interface QueryString {
  [key: string]: any;
}

export interface BrokerRequestHeader {
  [key: string]: string;
}

export interface BrokerRequest {
  path: string;
  method?: string;
  queryString?: QueryString;
  headers?: Array<BrokerRequestHeader>;
  data?: any;
}

export class BrokerSubscriber<T> extends EventEmitter<T> {
  constructor(private socket: any, private subscriberId: string) {
    super();
  }

  unsubscribe() {
    console.log('brokersub: removing event listener for subscription ' + this.subscriberId);
    this.socket.removeListener(this.subscriberId);
    console.log('brokersub: emitting endSubscription command for subscription ' + this.subscriberId);
    this.socket.emit('endSubscription', this.subscriberId);
  }
}

@Injectable()
export class BrokerService extends BaseService {

  public connecting: BehaviorSubject<boolean>;
  public connected: BehaviorSubject<boolean>;
  public disconnected: BehaviorSubject<boolean>;
  public reconnecting: BehaviorSubject<boolean>;
  public initialized: BehaviorSubject<boolean>;
  public reconnected: BehaviorSubject<boolean>;
  public reconnectFailed: BehaviorSubject<boolean>;
  public afterConnect: BehaviorSubject<boolean>;

  private _connecting: boolean;
  private _subscribers: Map<string, BehaviorSubject<any>>;
  private _subscriptionClientId: string;
  private _socket: any;
  private _loadSocketIOTimeout: any;

  constructor(
    private http: Http,
    private urlFactory: UrlFactoryService,
    private storage: StorageService,
    private store: Store<any>
  ) {
    super();
    this._connecting = false;
    this._subscribers = new Map<string, BehaviorSubject<any>>();

    this.connecting = new BehaviorSubject<boolean>(false);
    this.connected = new BehaviorSubject<boolean>(false);
    this.disconnected = new BehaviorSubject<boolean>(false);
    this.reconnecting = new BehaviorSubject<boolean>(false);
    this.reconnectFailed = new BehaviorSubject<boolean>(false);
    this.initialized = new BehaviorSubject<boolean>(false);
    this.reconnected = new BehaviorSubject<boolean>(false);
    this.afterConnect = new BehaviorSubject<boolean>(false);

    // NOTE have to reference the store blind here because auth depends on core
    this.store.select(s => s.authentication.initialized).distinctUntilChanged().subscribe(initialized => {
      console.log('broker: authentication initialized', initialized);
      if (initialized) {
        this.store.select(s => s.authentication.authenticated).distinctUntilChanged().subscribe(authenticated => {
          console.log('broker: auth changed', authenticated);
          if (authenticated) {
            console.log('broker: now authenticated, initalizing broker');
            this._init({ connectStream: true }).then(res => {
              console.log('broker: initialization complete');
            }, err => {
              console.error('broker: error intializing broker after auth change', err);
            });
          } else {
            console.log('broker: no longer authenticated, disconnecting broker');
            this._disconnect();
          }
        });
      }
    });
  }

  call(brokerReq: BrokerRequest): Promise<any> {
    console.log('broker:call()', brokerReq);
    return new Promise((resolve, reject) => {
      var request = this.buildRequest(brokerReq);

      this.http.request(request)
        .subscribe(
        res => {
          var data = res.json();
          // console.debug('broker: http success response', data);
          resolve(data);
        },
        err => {
          console.error('error making broker request', brokerReq, err);
          reject(err);
        });
    });
  }

  // use this when non-JSON is returned from broker
  callNonJSON(brokerReq: BrokerRequest): Promise<any> {
    console.log('broker:callNonJSON()', brokerReq);
    return new Promise((resolve, reject) => {
      var request = this.buildRequest(brokerReq);

      this.http.request(request)
        .subscribe(
        res => {
          // console.debug('broker: http success response - non-JSON returned', res);
          resolve(res);
        },
        err => {
          console.error('error making broker request', brokerReq, err);
          reject(err);
        });
    });
  }

  subscribe(brokerReq: BrokerRequest): Promise<BrokerSubscriber<any>> {
    console.debug('broker:subscribe()', brokerReq);
    return new Promise((resolve, reject) => {
      brokerReq.queryString = brokerReq.queryString || [];
      brokerReq.queryString = Object.assign({}, brokerReq.queryString || {}, { 'SubscriptionClient': this._subscriptionClientId, 'JWT': this.storage.get('authToken') });

      var httpRequest: Request = this.buildRequest(brokerReq);
      console.debug('broker: http request prepared', httpRequest);
      this.http.request(httpRequest).subscribe(res => {
        var data = res.json();
        console.log('request response received', data);
        if (data.subscriptionId) {
          // create a new subcriber based on an BehaviorSubject that also provides the consumer the
          // ability to unsubscribe
          var subscriber = new BrokerSubscriber<any>(this._socket, data.subscriptionId);

          // when a message is recieved on the socket for the given subscriptionId, invoke the
          // next() method on the obervable which causes a subscribed consumer to receive the
          // message
          this._socket.on(data.subscriptionId, (msg: any) => {
            console.debug('broker: subscription message received for ' + data.subscriptionId, msg);
            subscriber.next(msg);
          });

          // tell the socket server to start the subscription
          this._socket.emit('startSubscription', data.subscriptionId);

          // return the observable to the consumer
          resolve(subscriber);
        } else {
          console.error('broker: no subscriptionId received from server');
          reject({ message: 'subscription not created' });
        }
      }, (err: any) => {
        console.error('error making request', brokerReq, httpRequest, err);
        reject(err);
      });
    });
  }

  /**
   * Accepts BrokerRequest, and returns an observable
   * that subscribes to the endpoint ONLY upon calling
   * subscribe(). Also waits for Broker to be connected,
   * so the service doesn't have to bother.
   */
  getObservable<T>(incomingRequest: BrokerRequest): Observable<T> {
    return new Observable<T>((observer: Observer<T>) => {

      let _subscriptionId: string;

      const next = (data: T) => {
        observer.next(data);
      };

      const error = (error: any) => {
        observer.next(error);
      };

      this.connected
        .filter(c => !!c)
        .distinctUntilChanged()
        .flatMap(connected => {

          const queryString: QueryString = Object.assign(
            {},
            incomingRequest.queryString,
            {
              SubscriptionClient: this._subscriptionClientId,
              JWT: this.storage.get('authToken')
            }
          );

          const brokerRequest: BrokerRequest = Object.assign(
            {},
            incomingRequest,
            {
              queryString: queryString
            }
          );

          const httpRequest = this.buildRequest(brokerRequest);

          return this.http.request(httpRequest)
            .map(r => r.json())
            .filter((r: { subscriptionId?: string }) => !!r.subscriptionId)
            .map((r: any) => r.subscriptionId);

        }).subscribe(
        id => {

          _subscriptionId = id;

          if (!this._socket.hasListeners(_subscriptionId)) {
            this._socket.emit('startSubscription', _subscriptionId);
          }

          this._socket.on(_subscriptionId, next);
        },
        error
        );

      return (): TeardownLogic => {

        // NOTE the socket will be closed when the current authentication
        // is cleared, but the subscription might only be ended when
        // the component owning it is unloaded, so check to see that we
        // still have a socket to work with.
        if (this._socket) {
          // It's theoretically possible
          // to unsubscribe from an observable
          // before we've had a change to actually
          // listen, so make this conditional
          if (!!_subscriptionId) {
            this._socket.off(_subscriptionId, next);
          }

          // If there are no other listeners,
          // we can safely stop the subscription
          if (!this._socket.hasListeners(_subscriptionId)) {
            this._socket.emit('endSubscription', _subscriptionId);
          }
        }
      }

    });
  }

  // TODO this should be wrapped up in the observable returned from subscribe
  unsubscribe(subscriberId: string): void {
    var emitter: BehaviorSubject<any> = this._subscribers.get(subscriberId);
    if (emitter) { // just checking to see that we know what this is
      this._socket.removeListener(subscriberId);
      this._socket.emit('endSubscription', subscriberId);
      this._subscribers.delete(subscriberId);
    }
  }

  private _init(options: any): Promise<any> {
    var that = this;
    return new Promise((resolve, reject) => {

      this._disconnect().then(res => {
        // NOTE avoid parallel requests to initialize broker
        if (!that._connecting) {
          that._connecting = true;

          that._loadSocketIOTimeout = setTimeout(() => {
            console.error('error loading socket.io, is broker running?');
            that.manualDisconnect();
          }, 60000);

          let jwt = this.storage.get('authToken');
          // console.log('broker: (REMOVE THIS) JWT prior to socket.io lib request', jwt);
          if (!jwt || jwt.length === 0) {
            reject({ message: 'missing or invalid JWT in context for broker init', severity: 'FATAL' });
          }

          var url = that.urlFactory.getBrokerURL('/socket.io/socket.io.js', {
            'JWT': jwt
          });
          // console.log('get socket.io url is ' + url);

          // TODO reevaluate this
          // NOTE ng2 http does not yet support examining response headers, so we'll have to get socket.io every time
          // var socketIOMD5 = this.storage.get('socketIOMD5');
          var headers = new Headers();

          var options = {
            headers: headers
          };

          // !!! TODO add preflight OPTIONS request with Access-Control-RequestMethod and Origin headers to enable CORS
          that.http.request(url, options).subscribe(res => {
            var data = res.text();
            clearTimeout(that._loadSocketIOTimeout);
            that._loadSocketIOTimeout = null;

            globalEval(data);
            that.connect().then(
              (result) => {
                this._connecting = false;
                resolve(result);
              },
              (err) => {
                console.error('broker: unable to initialize broker socket connection, subscriptions are unavaialble', err);
                this._connecting = false;
                reject(err);
              });
          }, (err: any) => {
            clearTimeout(that._loadSocketIOTimeout);
            that._loadSocketIOTimeout = null;

            this._connecting = false;
            console.error('broker: error getting socket.io', err);
            reject(err);
          });
        }
      }, err => {
        console.log('broker: error disconnecting prior to re-init', err);
        reject(err);
      });
    });
  }

  endAllSubscriptions() {
    console.log('broker: endAllSubscriptions');

    for (var k in this._subscribers) {
      this.unsubscribe(k);
    }
    this._subscribers.clear();

    this._socket.removeAllListeners();
    this._socket.emit('endAllSubscriptions');
  }

  /**
   * @deprecated
   * @returns {boolean}
   * @memberOf BrokerService
   */
  isConnected(): boolean {
    console.warn('broker.svc: isConnected() is deprecated.  Subscribe to connected instead');
    return (typeof this._socket !== 'undefined' && typeof this._subscriptionClientId !== 'undefined');
  }

  /**
   * @see manualDisconnect
   * This function is used to allow a consumer to forcce the service to reconnect to Broker.  This
   * can be important in situations where, for example, an app on a mobile device is suspended and then
   * resumed.  In this case it is important to force this service to rebuild its internal state. Since
   * it's not known how long the app was suspended.
   */
  manualReconnect(): Promise<any> {
    return this.connect();
  }

  /**
   * @see manualReconnect
   * This method is called to force this service to disconnect from Broker. This can be important for cases
   * where, for example, an app on a mobile device is being suspended.  In this case it can take several minutes
   * for the connection to broker to disconnect due to inactivity.  It is useful therefore to be able to force
   * the service to disconnect as the app is being suspended.
   */
  manualDisconnect(): Promise<any> {
    return this._disconnect();
  }

  private connect(): Promise<any> {
    var that = this;
    return new Promise(function(resolve, reject) {

      that.connected.take(1).subscribe(connected => {

        if (connected) {
          that._disconnect();
        }

        var options: any = {};
        // NOTE assume https protocol here
        options['connect timeout'] = 100;
        options['forceNew'] = true;
        options['query'] = 'JWT=' + that.storage.get('authToken');
        options['transports'] = ['polling', 'websocket'];

        var url = that.urlFactory.getBrokerURL();

        that.connecting.next(true);

        that._socket = window.io.connect(url, options);
        that._socket.heartbeatTimeout = 10000;
        that._socket.closeTimeout = 10000;

        that.afterConnect.next(true);

        that._socket.on('connect', function() {
          console.log('broker: connected');

          if (that._socket.io && that._socket.io.engine) {
            that._subscriptionClientId = that._socket.io.engine.id;
          } else {
            that._subscriptionClientId = that._socket.socket.sessionId;
          }

          if (!that._subscriptionClientId) {
            console.error('broker: error initalizing broker api, no subscription client id received');
            reject({ message: 'no subscription client id received' });
            that.connected.next(false);
          } else {
            console.log('broker: subscriptionClientId is ' + that._subscriptionClientId);
            that.connected.next(true);
            resolve();
          }
        });

        that._socket.on('disconnect', reason => {
          console.debug('DEBUG >> broker.service.ts:452: socket disconnected: %s', reason);
          that.disconnected.next(true);

          // We can't know _why_ we lost the connection,
          // just that it's lost. It's the responsibility
          // of the MonitoredHttpService to work out whether
          // the JWT is good
          that.http.setJwtStatus(JWT_STATUS.UNKNOWN);

          // However, the connection itself is almost
          // certainly down...
          that.http.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        });

        that._socket.on('reconnect', attempt => {
          console.debug('DEBUG >> broker.service.ts:457: socket reconnected after %s attempts', attempt);
          that.reconnected.next(true);
          that.http.setJwtStatus(JWT_STATUS.VALID);
          that.http.setConnectionStatus(CONNECTION_STATUS.CONNECTED);
        });

        that._socket.on('reconnecting', attempt => {
          console.warn('WARN >> broker.service.ts:466: socket reconnection attempt %s', attempt);
          that.reconnecting.next(true);
        });

        that._socket.on('reconnect_failed', () => {
          console.error('ERROR >> broker.service.ts:475: socket reconnect failed');
          that.reconnectFailed.next(true);
        });

        that._socket.once('connect', () => {
          that.initialized.next(true);
        });

        that._socket.on('connect', () => {
          console.debug('LOG >> broker.service.ts:440: socket connected');
          that.http.setJwtStatus(JWT_STATUS.VALID);
          that.http.setConnectionStatus(CONNECTION_STATUS.CONNECTED);
        });

      });

    });
  }

  private _disconnect(): Promise<any> {
    var handler = (resolve: Function, reject: Function) => {
      try {
        if (this._socket) {
          if (this._socket.disconnectSync) {
            this._socket.disconnectSync();
          } else {
            this._socket.disconnect();
          }
        }

        if (this._socket) {
          this._socket.removeAllListeners();
          delete this._socket;
          if (typeof (window.io) !== 'undefined' && window.io.sockets) {
            window.io.sockets = {};
          }
        }

        this.connected.next(false);

        resolve();
      } catch (e) {
        reject(e);
      }
    }
    return new Promise(handler);
  }

  private buildRequest(request: BrokerRequest, subscriptionClientId?: string): Request {
    var options: BaseRequestOptions = new BaseRequestOptions();
    options = options.merge({
      url: this.buildURL(request),
      method: this.getRequestMethod(request.method)
    });

    var headers = this.buildHeaders(request.headers);
    if (options.method === RequestMethod.Post || options.method === RequestMethod.Put || options.method === RequestMethod.Delete) {
      if (request.data) {
        headers.append('Content-Type', 'application/json');
        options = options.merge({
          body: JSON.stringify(request.data)
        });
      }
    }

    options = options.merge({
      headers: headers,
      // NOTE the following is required to allow the JWT cookie to be sent for ng2 http requests
      // for the case where the app is accepting a JWT in the query string at startup
      withCredentials: true
    });

    return new Request(options);
  }

  private buildURL(request: BrokerRequest): string {
    return this.urlFactory.getBrokerURL(request.path, request.queryString);
  }

  private buildHeaders(additionalHeaders?: Array<BrokerRequestHeader>): Headers {
    var headers = new Headers();
    headers.append('Authorization', 'Bearer ' + this.storage.get('authToken'));
    headers.append('Accept', 'application/json');

    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach(key => {
        headers.append(key, additionalHeaders[key]);
      });
    }

    return headers;
  }

  private getRequestMethod(given: string): RequestMethod {
    switch (given) {
      case 'GET':
        return RequestMethod.Get;
      case 'POST':
        return RequestMethod.Post;
      case 'PUT':
        return RequestMethod.Put;
      case 'DELETE':
        return RequestMethod.Delete;
      default:
        return RequestMethod.Get;
    }
  }
}

// Non-jQuery global eval function
var globalEval = (function(global, realArray, indirectEval, indirectEvalWorks?) {
  try {
    eval('var Array={};');
    indirectEvalWorks = indirectEval('Array') == realArray;
  } catch (err) { }

  return indirectEvalWorks
    ? indirectEval
    : (global.execScript
      ? function(expression) {
        global.execScript(expression);
      }
      : function(expression) {
        setTimeout(expression, 0);
      });
})(this, Array, window.eval);




// WEBPACK FOOTER //
// ./~/@c4/core/src/app/services/broker.service.ts